import React from "react";

/**
 * App-wide error boundary. Without this, any render-time throw in a child
 * (a malformed API response, a null deref, a dead lazy import) white-screens
 * the entire admin panel with no recovery path. This catches the crash and
 * offers a Reload that also clears any corrupt local/session storage that may
 * have triggered it.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep a console trace for debugging; no external logger is wired here.
    // eslint-disable-next-line no-console
    console.error("Admin panel crashed:", error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.assign("/admin/user");
  };

  handleReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {
      /* ignore */
    }
    window.location.assign("/credentials/login");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
          padding: "24px",
          textAlign: "center",
          fontFamily: "system-ui, sans-serif",
          background: "#F8F9FA",
        }}
      >
        <h2 style={{ margin: 0, color: "#1c1c1c" }}>Something went wrong</h2>
        <p style={{ margin: 0, color: "#666", maxWidth: 420 }}>
          This page hit an unexpected error. Try reloading. If it keeps
          happening, sign in again to clear any stale session data.
        </p>
        <div style={{ display: "flex", gap: "12px", marginTop: "8px" }}>
          <button
            onClick={this.handleReload}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              background: "var(--admin-page--color, #f99234)",
              color: "white",
              fontSize: "0.95rem",
            }}
          >
            Reload
          </button>
          <button
            onClick={this.handleReset}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "1px solid #ccc",
              cursor: "pointer",
              background: "white",
              color: "#1c1c1c",
              fontSize: "0.95rem",
            }}
          >
            Sign in again
          </button>
        </div>
      </div>
    );
  }
}
